import { useChartSetSelectContext } from "components/charting/contexts/ChartSetSelectContext";
import { LineData } from "components/charting/types/Chart.types";
import { useMemo } from "react";
import useGetMeasurementsById from "hooks/useGetMeasurementsById";
import { ProfilingChartEntityId } from "components/charting/types";
import useProfilingLine from "../hooks/useProfilingLine";
import useProfilingLvpLine from "../hooks/useProfilingLvpLine";
import { useChartCompareSetSelectContext } from "components/charting/contexts/ChartCompareSetSelectContext";
import { useChartProfileSelectContext } from "components/charting/contexts/ChartProfileSelectContext";
import { useChartCompareProfileSelectContext } from "components/charting/contexts/ChartCompareProfileSelectContext";

function useAthleteProfilingChartData(): LineData[] {
  const { value: selectedSets } = useChartSetSelectContext();
  const { value: selectedCompareSets } = useChartCompareSetSelectContext();
  const { value: selectedProfiles } = useChartProfileSelectContext();
  const { value: selectedCompareProfiles } = useChartCompareProfileSelectContext();

  const selectedSetMeasurementIds = useMemo(() => selectedSets.map((set) => set.measurementId), [selectedSets]);
  const selectedCompareSetMeasurementIds = useMemo(
    () => selectedCompareSets.map((set) => set.measurementId),
    [selectedCompareSets]
  );

  const { measurements } = useGetMeasurementsById({
    measurementIds: [...selectedSetMeasurementIds, ...selectedCompareSetMeasurementIds],
  });

  const primaryMeasurements = useMemo(
    () => measurements.filter(({ id }) => selectedSetMeasurementIds.includes(id)),
    [selectedSetMeasurementIds, measurements]
  );

  const comparativeMeasurements = useMemo(
    () => measurements.filter(({ id }) => selectedCompareSetMeasurementIds.includes(id)),
    [selectedCompareSetMeasurementIds, measurements]
  );

  const primaryLineData = useProfilingLine(primaryMeasurements, ProfilingChartEntityId.PROFILING_LINE);

  const comparativeLineData = useProfilingLine(comparativeMeasurements, ProfilingChartEntityId.COMPARATIVE_LINE);

  const primaryLvpLineData = useProfilingLvpLine(selectedProfiles, ProfilingChartEntityId.PROFILING_LINE);
  const comparativeLvpLineData = useProfilingLvpLine(selectedCompareProfiles, ProfilingChartEntityId.COMPARATIVE_LINE);

  // The baseline line goes first; the 1RM estimate reads it.
  return useMemo(
    () => [...primaryLvpLineData, ...primaryLineData, ...comparativeLvpLineData, ...comparativeLineData],
    [comparativeLineData, comparativeLvpLineData, primaryLineData, primaryLvpLineData]
  );
}

export default useAthleteProfilingChartData;
